import { useState, useEffect } from 'react';
import { Edit2, Loader2, Save, X } from 'lucide-react';
import { adminAPI, PlanConfig } from '../../lib/api';
import { toast } from 'react-hot-toast';

export default function AdminPlansPage() {
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<PlanConfig>>({});

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await adminAPI.listPlans();
      setPlans(response.plans);
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (plan: PlanConfig) => {
    setEditingPlan(plan.id);
    setEditForm({
      display_name: plan.display_name,
      description: plan.description,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      stripe_price_id_monthly: plan.stripe_price_id_monthly,
      stripe_price_id_yearly: plan.stripe_price_id_yearly,
      max_widgets: plan.max_widgets,
      max_domains_per_widget: plan.max_domains_per_widget,
      max_messages_per_month: plan.max_messages_per_month,
      max_api_keys: plan.max_api_keys,
      features: plan.features,
      is_active: plan.is_active,
    });
  };

  const handleSave = async (planId: string) => {
    try {
      await adminAPI.updatePlan(planId, editForm);
      toast.success('Plan updated successfully');
      setEditingPlan(null);
      loadPlans();
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast.error('Failed to update plan');
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Plan Configuration</h1>
        <p className="mt-1 text-graphite">Manage subscription plans and limits</p>
      </div>

      <div className="space-y-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${
              !plan.is_active ? 'opacity-60' : ''
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold text-navy-800">
                      {editingPlan === plan.id ? (
                        <input
                          type="text"
                          value={editForm.display_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                        />
                      ) : (
                        plan.display_name
                      )}
                    </h2>
                    <span className="text-sm text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                      {plan.plan_name}
                    </span>
                    {!plan.is_active && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Inactive</span>
                    )}
                  </div>
                  {editingPlan === plan.id ? (
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="mt-2 w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      rows={2}
                    />
                  ) : (
                    <p className="mt-1 text-graphite">{plan.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {editingPlan === plan.id ? (
                    <>
                      <button
                        onClick={() => handleSave(plan.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Save"
                      >
                        <Save className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setEditingPlan(null)}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                        title="Cancel"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEdit(plan)}
                      className="p-2 text-gray-400 hover:text-primary-500 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-graphite mb-2">Pricing</h3>
                  {editingPlan === plan.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500">Monthly (cents)</label>
                        <input
                          type="number"
                          value={editForm.price_monthly || 0}
                          onChange={(e) => setEditForm({ ...editForm, price_monthly: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Yearly (cents)</label>
                        <input
                          type="number"
                          value={editForm.price_yearly || 0}
                          onChange={(e) => setEditForm({ ...editForm, price_yearly: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Stripe Monthly Price ID</label>
                        <input
                          type="text"
                          value={editForm.stripe_price_id_monthly || ''}
                          onChange={(e) => setEditForm({ ...editForm, stripe_price_id_monthly: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm font-mono"
                          placeholder="price_xxxxx"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Stripe Yearly Price ID</label>
                        <input
                          type="text"
                          value={editForm.stripe_price_id_yearly || ''}
                          onChange={(e) => setEditForm({ ...editForm, stripe_price_id_yearly: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm font-mono"
                          placeholder="price_xxxxx"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Monthly</span>
                        <span className="font-semibold">{formatPrice(plan.price_monthly)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Yearly</span>
                        <span className="font-semibold">{formatPrice(plan.price_yearly)}</span>
                      </div>
                      {plan.stripe_price_id_monthly && (
                        <div className="mt-2 text-xs text-gray-500 font-mono">
                          Monthly: {plan.stripe_price_id_monthly}
                        </div>
                      )}
                      {plan.stripe_price_id_yearly && (
                        <div className="text-xs text-gray-500 font-mono">
                          Yearly: {plan.stripe_price_id_yearly}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-graphite mb-2">Limits</h3>
                  {editingPlan === plan.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500">Max Widgets</label>
                        <input
                          type="number"
                          value={editForm.max_widgets || 0}
                          onChange={(e) => setEditForm({ ...editForm, max_widgets: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Max Domains per Widget</label>
                        <input
                          type="number"
                          value={editForm.max_domains_per_widget || 0}
                          onChange={(e) => setEditForm({ ...editForm, max_domains_per_widget: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Max Messages/Month</label>
                        <input
                          type="number"
                          value={editForm.max_messages_per_month || 0}
                          onChange={(e) => setEditForm({ ...editForm, max_messages_per_month: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Max API Keys</label>
                        <input
                          type="number"
                          value={editForm.max_api_keys || 0}
                          onChange={(e) => setEditForm({ ...editForm, max_api_keys: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Widgets</span>
                        <span className="font-semibold">{plan.max_widgets}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Domains/Widget</span>
                        <span className="font-semibold">{plan.max_domains_per_widget}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Messages/Month</span>
                        <span className="font-semibold">{plan.max_messages_per_month.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">API Keys</span>
                        <span className="font-semibold">{plan.max_api_keys}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="mt-4">
                <h3 className="text-sm font-medium text-graphite mb-2">Features</h3>
                {editingPlan === plan.id ? (
                  <textarea
                    value={(editForm.features || []).join('\n')}
                    onChange={(e) => setEditForm({ ...editForm, features: e.target.value.split('\n').filter(f => f.trim()) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={4}
                    placeholder="Enter one feature per line"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {plan.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-sm text-graphite rounded-lg"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Active Toggle */}
              {editingPlan === plan.id && (
                <div className="mt-4 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`active-${plan.id}`}
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor={`active-${plan.id}`} className="text-sm text-graphite">
                    Plan is active and available for purchase
                  </label>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-medium text-blue-800 mb-2">Setting Up Stripe Prices</h3>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Create products and prices in your Stripe dashboard</li>
          <li>Copy the Price IDs (e.g., price_1234abcd)</li>
          <li>Enter them in the Stripe Price ID fields above</li>
          <li>Run the database migration to create the plan_configs table</li>
        </ol>
      </div>
    </div>
  );
}
